import { useCallback } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

import ChevronLeft from './chevron-left.svg';
import ChevronRight from './chevron-right.svg';
import { IconButton } from '../../IconButton';
import { Button } from '../../Button';
import { Text } from '../../Text';
import { spacing } from '../../../theme';

export interface PaginationProps {
	currentPage: number;
	numberOfPages: number;
	rowsPerPage: number;
	onPageChange: (page: number) => void;
}

export const Pagination = ({ currentPage, numberOfPages, onPageChange }: PaginationProps) => {
	const handlePageChange = useCallback(
		(newPage: number) => () => {
			onPageChange(newPage);
		},
		[onPageChange],
	);

	const renderPrevious = useCallback(() => {
		if (currentPage === 0) {
			return null;
		}

		return (
			<IconButton
				size="small"
				appearance="ghost"
				Icon={ChevronLeft}
				onPress={handlePageChange(currentPage - 1)}
			/>
		);
	}, [currentPage, handlePageChange]);

	const renderNext = useCallback(() => {
		if (currentPage === numberOfPages - 1) {
			return null;
		}

		return (
			<IconButton
				size="small"
				appearance="ghost"
				Icon={ChevronRight}
				onPress={handlePageChange(currentPage + 1)}
			/>
		);
	}, [currentPage, numberOfPages, handlePageChange]);

	if (numberOfPages <= 1) {
		return null;
	}

	return (
		<View style={styles.root}>
			{renderPrevious()}

			<ScrollView horizontal contentContainerStyle={styles.pages}>
				{Array.from({ length: numberOfPages }).map((_, index) => (
					<Button
						key={index}
						appearance="ghost"
						color="primary"
						onPress={handlePageChange(index)}
						style={styles.pageBtn}
						disabled={index === currentPage}
					>
						<Text category="h6">{index + 1}</Text>
					</Button>
				))}
			</ScrollView>

			{renderNext()}
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
		marginTop: spacing(1),
		gap: spacing(2),
	},
	pages: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: spacing(2),
		flexGrow: 1,
		alignSelf: 'flex-end',
	},
	pageBtn: {
		width: 32,
		height: 32,
		minWidth: 32,
		minHeight: 32,
		borderRadius: 16,
		paddingHorizontal: 0,
	},
});
